/**
 * /create — Create: the five ways to start a song, and the result of doing so.
 *
 * Redesigned in the Studio pass. The prototype stacked a heading, a label, a
 * 3-row textarea, eight chips and a button as one flat column, and dropped the
 * finished song into a bordered card underneath. The pieces are the same; the
 * arrangement now answers the three questions the workspace has to answer —
 * what am I creating (the composer), what with (its controls), what did I make
 * (the result) — in that order, with one of them dominant at a time.
 *
 * THE INFORMATION ARCHITECTURE, which is what changed most recently. Create is the
 * section that holds every way of making a song, and there are five: a prompt, Music
 * DNA, the Genre Mixer, Image Music and Voice Cloner. Only the first was visible here
 * at first — the other three had routes and no entrance, one of them reachable solely
 * from a chevron nested under a sidebar row. CreateMethods now names all four routed
 * methods at the top of the screen, and the composer below it is labelled as the fifth
 * rather than being the unlabelled default.
 *
 * The four cards are LINKS to the existing routes. No generation logic is duplicated
 * and no screen is re-implemented here; each method keeps its own store and its own
 * behaviour, and the three song-generating ones keep their own call into
 * useSongs().createSong, which is the one funnel those three plus the composer share.
 *
 * WHY THE TITLE IS "CREATE" AND NOT "STUDIO". It read "Studio" from the monolith, when
 * the Studio was this screen. It is not any more: /studio and /library/:songId are the
 * workspace, they are gated on the Studio plan, and this screen is neither — an account
 * with no Studio plan creates songs here perfectly well. Two different places called
 * Studio, one of which the sidebar calls Create, was the last remnant of the navigation
 * defect that §3 split apart.
 *
 * The form values still live in createStore rather than in this component: Music
 * DNA ("Generate from this DNA") and the Vault's load-prompt / load-preset actions
 * push a prompt in from elsewhere and navigate here, so the state has to outlive
 * this route's mount. See src/stores/createStore.ts.
 */
import { useNavigate } from 'react-router-dom';
import {
  Composer,
  COMPOSER_ICONS,
  CreateMethods,
  GenerationStatus,
  StudioHeader,
  TrackResult,
} from '../components/studio';
import type { CreateMethodKey } from '../components/studio';
import { generationApi } from '../api/generationApi';
import { GENRES, LANGUAGES, OTHER_LANGUAGE, VOCALS } from '../data/music';
import { usePlayer, useSongs } from '../hooks';
import { MUSIC_MODEL_OPTIONS, STANDARD_MUSIC_MODEL } from '../lib/musicGenerationModels';
import { TEXT_MUSIC_GENERATION_CREDITS } from '../lib/credits';
import { useCreateStore } from '../stores/createStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { NO_DEFAULT_VOCAL, usePreferencesStore } from '../stores/preferencesStore';
import { useUiStore } from '../stores/uiStore';
import { paths } from './paths';

/** Lyria song (2) plus generated cover image (1). */
const GENERATION_COST = TEXT_MUSIC_GENERATION_CREDITS;

/**
 * Which methods carry the Preview tag, asked of the generation boundary rather than
 * hardcoded.
 *
 * Image Music is the one whose availability is a fact about the deployment: its screen
 * is complete up to the Lyria 3 request and cannot send it without a backend. Reading
 * `imageMusicSupport()` here means the tag disappears on its own the day that changes,
 * instead of being a constant someone has to remember to delete. Text-to-music, Music
 * DNA and the Genre Mixer are all openly simulated and say so on their own screens —
 * see the PROTOTYPE notices there — so they are not marked twice.
 */
function previewMethods(): CreateMethodKey[] {
  return generationApi.imageMusicSupport().available ? [] : ['image'];
}

function comingSoonMethods(): CreateMethodKey[] {
  return ['voice'];
}

export default function Create() {
  const navigate = useNavigate();

  const prompt = useCreateStore((s) => s.prompt);
  const model = useCreateStore((s) => s.model);
  const genre = useCreateStore((s) => s.genre);
  const vocal = useCreateStore((s) => s.vocal);
  const language = useCreateStore((s) => s.language);
  const setPrompt = useCreateStore((s) => s.setPrompt);
  const setModel = useCreateStore((s) => s.setModel);
  const setGenre = useCreateStore((s) => s.setGenre);
  const setVocal = useCreateStore((s) => s.setVocal);
  const setLanguage = useCreateStore((s) => s.setLanguage);
  const defaultVocal = usePreferencesStore((s) => s.defaultVocal);
  const ownsProfessional = useEntitlementsStore((s) => s.professional);
  const resultSongId = useCreateStore((s) => s.resultSongIds.create);
  const setResultSongId = useCreateStore((s) => s.setSurfaceResultSongId);
  const effectiveVocal = vocal || (defaultVocal === NO_DEFAULT_VOCAL ? '' : defaultVocal);

  /*
    Whether the language picker applies at all.

    An instrumental track has no language, and generationApi stamps such a song
    "Instrumental" whatever is selected here — so rather than leave a control that looks
    live and is overruled, the row goes away and the reason takes its place. The two
    conditions are the same two that function checks, which is deliberate: if they ever
    disagree, the UI is lying about what the request will do.
  */
  const instrumental = genre === 'Lo-fi instrumental' || effectiveVocal === 'Instrumental only';

  const { generating, activeGenerationSurface, createSong, downloadSong, downloadLyrics, getSong } =
    useSongs();
  const { isPlaying, isActive, elapsed, bufferedSec, speed, loading, toggle, seek } = usePlayer();
  const setDeleteTarget = useUiStore((s) => s.setDeleteTarget);
  const showToast = useUiStore((s) => s.showToast);

  const generated = getSong(resultSongId ?? undefined);
  const ownsGeneratingUi = generating && activeGenerationSurface === 'create';
  const lockedByAnotherGeneration = generating && !ownsGeneratingUi;

  return (
    <div>
      <StudioHeader title="Create" subtitle="Five ways to turn an idea into a song." />

      {/* The other four methods, first — because the choice of how to start comes
          before filling anything in, and because a prompt box at the top of the screen
          reads as the only option. */}
      <CreateMethods preview={previewMethods()} comingSoon={comingSoonMethods()} />

      {/* ---- and the fifth ----
          Labelled now rather than being the untitled thing the page opens with. Four
          named cards above an unnamed textarea would imply the textarea belongs to one
          of them. */}
      <p className="lyx-ws-eyebrow">Or describe it yourself</p>

      <Composer
        prompt={prompt}
        onPromptChange={setPrompt}
        model={{
          label: 'Model',
          options: MUSIC_MODEL_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            locked: option.requiresOto2 && !ownsProfessional,
          })),
          value: ownsProfessional ? model : STANDARD_MUSIC_MODEL,
          onChange: setModel,
        }}
        primary={[
          {
            label: 'Genre',
            options: GENRES,
            value: genre,
            onChange: setGenre,
            clearable: true,
            icon: COMPOSER_ICONS.genre,
          },
        ]}
        advanced={[
          {
            label: 'Vocals',
            options: VOCALS,
            value: vocal,
            onChange: setVocal,
            clearable: true,
            icon: COMPOSER_ICONS.vocal,
            caution: !vocal
              ? defaultVocal === NO_DEFAULT_VOCAL
                ? 'Vocals will be derived from prompt if applicable'
                : `No vocal selected. Using your default: ${defaultVocal}.`
              : undefined,
          },
          /*
            Language sits beside Vocals inside the shared genre/voice/language section
            rather than in a separate band of its own.

            The two are one decision made twice — who sings and in what — so they belong
            together, and putting a list of ten chips above the Generate button would
            crowd the card the redesign deliberately quieted.
          */
          {
            label: 'Language',
            options: LANGUAGES,
            value: language,
            onChange: setLanguage,
            clearable: true,
            icon: COMPOSER_ICONS.language,
            note: instrumental ? 'Not used for an instrumental track.' : undefined,
            caution:
              !instrumental && language === OTHER_LANGUAGE
                ? 'Specify the language in your prompt. Your default language will not be used.'
                : !instrumental && !language
                  ? 'No language selected. Specify the language in your prompt.'
                  : undefined,
          },
        ]}
        onGenerate={async () => {
          // The selection reaches the request, and only when it can apply — see
          // `instrumental` above. Passing it regardless would be harmless (the API
          // overrules it) but would put a value in the body that contradicts the rest
          // of it, which is not what a backend should have to untangle.
          const song = await createSong(
            prompt,
            genre,
            GENERATION_COST,
            effectiveVocal,
            instrumental ? undefined : language === OTHER_LANGUAGE ? '' : language || undefined,
            {
              ownerSurface: 'create',
              model: ownsProfessional ? model : STANDARD_MUSIC_MODEL,
            }
          );
          if (song) setResultSongId('create', song.id);
        }}
        generating={ownsGeneratingUi}
        disabled={lockedByAnotherGeneration}
        disabledReason={
          lockedByAnotherGeneration ? 'Another generation is already in progress.' : undefined
        }
      />

      {/* One of these three at a time: working, the result, or nothing yet. */}
      {ownsGeneratingUi ? (
        <GenerationStatus />
      ) : generated ? (
        <TrackResult
          song={generated}
          playing={isPlaying(generated.id)}
          active={isActive(generated.id)}
          elapsed={elapsed}
          bufferedSec={bufferedSec}
          speed={speed}
          loading={loading && isActive(generated.id)}
          onTogglePlay={() => toggle(generated.id)}
          onSeek={(seconds) => seek(generated.id, seconds)}
          onOpenStudio={() => navigate(paths.song(generated.id))}
          onDownload={() => downloadSong(generated)}
          onDownloadLyrics={() => downloadLyrics(generated)}
          onCopyLyrics={() => {
            navigator.clipboard?.writeText(generated.lyrics ?? '');
            showToast('Lyrics copied to clipboard');
          }}
          onDismiss={() => setResultSongId('create', null)}
          onDelete={() => setDeleteTarget(generated, 'generatedResult')}
        />
      ) : null}
    </div>
  );
}
